import { Scene } from 'phaser';
import { Unit } from '../entities/Unit';
import { Enemy } from '../entities/Enemy';
import { POSITIONS, MOVEMENT, COMBAT_MECHANICS } from '../config/Constants';

export class CombatSystem {
  private scene: Scene;
  private playerUnits: Unit[];
  private enemies: Enemy[];
  private releasedChargers: Set<Unit> = new Set();

  constructor(scene: Scene) {
    this.scene = scene;
    this.playerUnits = [];
    this.enemies = [];
  }

  public addUnit(unit: Unit): void {
    this.playerUnits.push(unit);
  }

  public removeUnit(unit: Unit): void {
    const index = this.playerUnits.indexOf(unit);
    if (index > -1) {
      this.playerUnits.splice(index, 1);
    }
  }

  public addEnemy(enemy: Enemy): void {
    this.enemies.push(enemy);
  }

  public removeEnemy(enemy: Enemy): void {
    const index = this.enemies.indexOf(enemy);
    if (index > -1) {
      this.enemies.splice(index, 1);
    }
  }

  public update(): void {
    this.updateUnitMovement();
    this.updateEnemyMovement();
    this.resolveCombat();
  }

  private updateUnitMovement(): void {
    const screenHeight = this.scene.cameras.main.height;
    const halfScreen = screenHeight / 2;
    
    this.playerUnits.forEach(unit => {
      const unitType = unit.getUnitType();

      if (this.enemies.length > 0) {
        const nearestEnemy = this.findNearestEnemy(unit);
        if (nearestEnemy) {
          switch (unitType) {
            case 'tank':
              this.moveTank(unit, nearestEnemy, halfScreen);
              break;
            case 'ranged':
              this.moveRanged(unit, nearestEnemy, halfScreen);
              break;
            case 'charger':
              this.moveCharger(unit, nearestEnemy);
              break;
            default:
              this.moveDefault(unit, nearestEnemy);
              break;
          }
        }
      } else {
        // No enemies, move forward slowly but not past half screen
        if (unit.y > halfScreen + POSITIONS.HALF_SCREEN_NO_GO_ZONE) {
          unit.y -= MOVEMENT.DEFAULT_UNIT_SPEED;
          unit.graphics.y = unit.y;
        }
      }
    });
  }

  private updateEnemyMovement(): void {
    this.enemies.forEach(enemy => {
      const stats = enemy.getStats();
      const enemyType = enemy.getEnemyType();

      if (this.playerUnits.length > 0) {
        // Find nearest player unit
        const nearestUnit = this.findNearestUnit(enemy);
        if (nearestUnit) {
          const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, nearestUnit.x, nearestUnit.y);
          
          if (enemyType === 'ranged') {
            // Ranged enemies stop at attack range
            const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, nearestUnit.x, nearestUnit.y);
            const moveDistance = stats.speed * MOVEMENT.FRAME_RATE;
            
            if (distance > stats.range) {
              // Move toward target if too far
              enemy.x += Math.cos(angle) * moveDistance;
              enemy.y += Math.sin(angle) * moveDistance;
            }
            // Move sideways to align with target, maintain distance
            else {
              enemy.x += Math.cos(angle) * moveDistance * 0.3; // Slow lateral movement
            }
          } else {
            // Melee units walk toward target
            const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, nearestUnit.x, nearestUnit.y);
            const moveDistance = stats.speed * MOVEMENT.FRAME_RATE;
            
            enemy.x += Math.cos(angle) * moveDistance;
            enemy.y += Math.sin(angle) * moveDistance;
          }
          
          enemy.graphics.x = enemy.x;
          enemy.graphics.y = enemy.y;
        }
      } else {
        // No player units, move downward toward base
        enemy.y += stats.speed * MOVEMENT.FRAME_RATE;
        enemy.graphics.y = enemy.y;
      }
    });
  }





  private resolveCombat(): void {
    // Update movement state for chargers
    this.updateChargerMovementState();

    // Handle tank contact damage (both player and enemy)
    this.handleTankContactDamage();

    // Handle ranged combat
    this.handleRangedCombat();

    // Handle charger combat
    this.handleChargerCombat();

    // Check for player-enemy collisions (prevent overlapping)
    this.checkPlayerEnemyCollisions();

    // Check for player-unit collisions (units should not overlap)
    this.checkSameSideCollisions();

    // Check for enemy-enemy collisions (enemies should not overlap)
    this.checkEnemyCollisions();

    // Check for enemy-base collisions
    const enemiesToRemove: Enemy[] = [];
    this.enemies.forEach(enemy => {
      const baseLine = this.scene.cameras.main.height - POSITIONS.BASE_LINE_OFFSET;
      if (enemy.y >= baseLine) {
        this.scene.events.emit('enemy-reached-base', enemy);
        enemiesToRemove.push(enemy);
      }
    });

    // Remove enemies that reached base
    enemiesToRemove.forEach(enemy => {
      this.removeEnemy(enemy);
    });
  }

  private checkPlayerEnemyCollisions(): void {
    // Check for player-enemy collisions (prevent overlapping)
    for (let i = 0; i < this.playerUnits.length; i++) {
      for (let j = 0; j < this.enemies.length; j++) {
        const unit = this.playerUnits[i];
        const enemy = this.enemies[j];

        if (this.isColliding(unit, enemy)) {
          this.handlePlayerEnemyCollision(unit, enemy);
        }
      }
    }
  }

  private checkSameSideCollisions(): void {
    // Check for player-unit collisions (units should not overlap)
    for (let i = 0; i < this.playerUnits.length; i++) {
      for (let j = i + 1; j < this.playerUnits.length; j++) {
        const unit1 = this.playerUnits[i];
        const unit2 = this.playerUnits[j];

        if (this.isColliding(unit1, unit2)) {
          this.handleUnitCollision(unit1, unit2);
        }
      }
    }
  }

  private checkEnemyCollisions(): void {
    // Check for enemy-enemy collisions (enemies should not overlap)
    for (let i = 0; i < this.enemies.length; i++) {
      for (let j = i + 1; j < this.enemies.length; j++) {
        const enemy1 = this.enemies[i];
        const enemy2 = this.enemies[j];

        if (this.isColliding(enemy1, enemy2)) {
          this.handleEnemyCollision(enemy1, enemy2);
        }
      }
    }
  }

  private isColliding(obj1: any, obj2: any): boolean {
    const distance = Phaser.Math.Distance.Between(
      obj1.x, obj1.y,
      obj2.x, obj2.y
    );
    
    // Get collision radius based on unit type
    const radius1 = this.getCollisionRadius(obj1);
    const radius2 = this.getCollisionRadius(obj2);
    const combinedRadius = radius1 + radius2;
    
    return distance < combinedRadius;
  }

  private getCollisionRadius(obj: any): number {
    if (obj instanceof Unit) {
      const unitType = obj.getUnitType();
      switch (unitType) {
        case 'tank': return 12; // Tank is 20x20, so radius ~10
        case 'ranged': return 10; // Ranged has radius 10
        case 'charger': return 10; // Charger triangle fits in ~10 radius
        default: return 8;
      }
    } else if (obj instanceof Enemy) {
      const enemyType = obj.getEnemyType();
      switch (enemyType) {
        case 'melee': return 10; // Enemy melee is 18x18, so radius ~9
        case 'ranged': return 8; // Enemy ranged has radius 8
        default: return 8;
      }
    }
    return MOVEMENT.UNIT_COLLISION_RADIUS;
  }

  private handlePlayerEnemyCollision(unit: Unit, enemy: Enemy): void {
    // Stable collision resolution to prevent flickering
    const angle = Phaser.Math.Angle.Between(unit.x, unit.y, enemy.x, enemy.y);
    
    // Get collision radii and optimal combat distance
    const radius1 = this.getCollisionRadius(unit);
    const radius2 = this.getCollisionRadius(enemy);
    const minDistance = Math.max(radius1 + radius2 + 3, this.getOptimalCombatDistance(unit, enemy) * 0.8);
    const currentDistance = Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y);
    
    if (currentDistance < minDistance) {
      // Calculate separation needed, but limit to prevent overshooting
      const separation = Math.min((minDistance - currentDistance) * 0.5, 3);
      
      // Apply separation with dampening
      unit.x -= Math.cos(angle) * separation;
      unit.y -= Math.sin(angle) * separation;
      enemy.x += Math.cos(angle) * separation;
      enemy.y += Math.sin(angle) * separation;
      
      // Update graphics positions
      unit.graphics.x = unit.x;
      unit.graphics.y = unit.y;
      enemy.graphics.x = enemy.x;
      enemy.graphics.y = enemy.y;
    }
  }

  private handleUnitCollision(unit1: Unit, unit2: Unit): void {
    // Stable collision resolution to prevent flickering
    const angle = Phaser.Math.Angle.Between(unit1.x, unit1.y, unit2.x, unit2.y);
    const currentDistance = Phaser.Math.Distance.Between(unit1.x, unit1.y, unit2.x, unit2.y);
    
    // Get collision radii
    const radius1 = this.getCollisionRadius(unit1);
    const radius2 = this.getCollisionRadius(unit2);
    const minDistance = radius1 + radius2 + 3; // Add small buffer
    
    if (currentDistance < minDistance) {
      // Calculate separation needed, but limit to prevent overshooting
      const separation = Math.min((minDistance - currentDistance) * 0.5, 2);
      
      // Apply separation with dampening
      unit1.x -= Math.cos(angle) * separation;
      unit1.y -= Math.sin(angle) * separation;
      unit2.x += Math.cos(angle) * separation;
      unit2.y += Math.sin(angle) * separation;
      
      // Update graphics positions
      unit1.graphics.x = unit1.x;
      unit1.graphics.y = unit1.y;
      unit2.graphics.x = unit2.x;
      unit2.graphics.y = unit2.y;
    }
  }

  private handleEnemyCollision(enemy1: Enemy, enemy2: Enemy): void {
    // Stable collision resolution to prevent flickering
    const angle = Phaser.Math.Angle.Between(enemy1.x, enemy1.y, enemy2.x, enemy2.y);
    const currentDistance = Phaser.Math.Distance.Between(enemy1.x, enemy1.y, enemy2.x, enemy2.y);
    
    // Get collision radii
    const radius1 = this.getCollisionRadius(enemy1);
    const radius2 = this.getCollisionRadius(enemy2);
    const minDistance = radius1 + radius2 + 3; // Add small buffer
    
    if (currentDistance < minDistance) {
      // Calculate separation needed, but limit to prevent overshooting
      const separation = Math.min((minDistance - currentDistance) * 0.5, 2);
      
      // Apply separation with dampening
      enemy1.x -= Math.cos(angle) * separation;
      enemy1.y -= Math.sin(angle) * separation;
      enemy2.x += Math.cos(angle) * separation;
      enemy2.y += Math.sin(angle) * separation;
      
      // Update graphics positions
      enemy1.graphics.x = enemy1.x;
      enemy1.graphics.y = enemy1.y;
      enemy2.graphics.x = enemy2.x;
      enemy2.graphics.y = enemy2.y;
    }
  }

  private getOptimalCombatDistance(unit: Unit, enemy: Enemy): number {
    const unitType = unit.getUnitType();
    const enemyType = enemy.getEnemyType();
    
    // Melee vs melee: stay very close but not overlapping
    if (unitType === 'tank' && (enemyType === 'melee' || !enemyType)) {
      return COMBAT_MECHANICS.TANK_CONTACT_RANGE;
    }
    
    // Melee vs ranged: close distance for melee to engage
    if (unitType === 'tank' && enemyType === 'ranged') {
      return 30;
    }
    
    // Ranged vs any: maintain some distance
    if (unitType === 'ranged') {
      return Math.min(unit.getStats().range * 0.8, 50);
    }
    
    // Charger: get very close for collision damage
    if (unitType === 'charger') {
      return MOVEMENT.CHARGER_COLLISION_DISTANCE;
    }
    
    // Default distance
    return 25;
  }



  private findNearestEnemy(unit: Unit): Enemy | null {
    if (this.enemies.length === 0) return null;

    let nearestEnemy: Enemy | null = null;
    let minDistance = Infinity;

    this.enemies.forEach(enemy => {
      const distance = Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y);
      if (distance < minDistance) {
        minDistance = distance;
        nearestEnemy = enemy;
      }
    });

    return nearestEnemy;
  }

  private findNearestUnit(enemy: Enemy): Unit | null {
    if (this.playerUnits.length === 0) return null;

    let nearestUnit: Unit | null = null;
    let minDistance = Infinity;

    this.playerUnits.forEach(unit => {
      const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, unit.x, unit.y);
      if (distance < minDistance) {
        minDistance = distance;
        nearestUnit = unit;
      }
    });

    return nearestUnit;
  }

  private moveTank(unit: Unit, nearestEnemy: Enemy, halfScreen: number): void {
    // Tanks form the frontline, never go past half screen
    const moveDistance = unit.getStats().speed * MOVEMENT.FRAME_RATE;
    
    // Move toward enemy but don't go past half screen
    const angle = Phaser.Math.Angle.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
    const newX = unit.x + Math.cos(angle) * moveDistance;
    const newY = unit.y + Math.sin(angle) * moveDistance;
    
    // Clamp Y position to not go past half screen
    if (newY <= halfScreen) {
      unit.x = newX;
      unit.y = halfScreen;
    } else {
      unit.x = newX;
      unit.y = newY;
    }
    
    unit.graphics.x = unit.x;
    unit.graphics.y = unit.y;
  }

  private moveRanged(unit: Unit, nearestEnemy: Enemy, halfScreen: number): void {
    // Ranged units stay behind tanks with safe distance
    const tankFrontLine = this.findTankFrontLineForRanged();
    const safeDistance = POSITIONS.RANGED_SAFE_DISTANCE;
    const moveDistance = unit.getStats().speed * MOVEMENT.FRAME_RATE;
    
    // Target position behind tanks but within range
    const targetY = Math.max(tankFrontLine + safeDistance, halfScreen + POSITIONS.TANK_FRONTLINE_BUFFER);
    const angle = Phaser.Math.Angle.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
    
    // Move sideways to align with enemy, but maintain distance
    unit.x += Math.cos(angle) * moveDistance;
    
    // Move toward target Y position
    if (Math.abs(unit.y - targetY) > 5) {
      const yDirection = unit.y > targetY ? -1 : 1;
      unit.y += yDirection * moveDistance * MOVEMENT.RANGED_Y_SPEED_RATIO;
    }
    
    unit.graphics.x = unit.x;
    unit.graphics.y = unit.y;
  }

  private moveCharger(unit: Unit, nearestEnemy: Enemy): void {
    // Chargers stay near base until released
    const baseLine = this.scene.cameras.main.height - POSITIONS.BASE_LINE_OFFSET;
    const moveDistance = unit.getStats().speed * MOVEMENT.FRAME_RATE;
    
    // Check if charger should be released
    const isReleased = this.isChargerReleased(unit);
    
    if (isReleased && nearestEnemy) {
      // Released: Run aggressively toward nearest enemy
      const angle = Phaser.Math.Angle.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
      unit.x += Math.cos(angle) * moveDistance * MOVEMENT.CHARGER_SPEED_MULTIPLIER;
      unit.y += Math.sin(angle) * moveDistance * MOVEMENT.CHARGER_SPEED_MULTIPLIER;
      
      // Check collision with enemy for damage application
      const distance = Phaser.Math.Distance.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
      if (distance < MOVEMENT.CHARGER_COLLISION_DISTANCE) {
        // Chargers do double damage when moving
        let damage = unit.getStats().damage;
        if (unit.getIsMoving()) {
          damage *= COMBAT_MECHANICS.CHARGER_MOVING_DAMAGE_MULTIPLIER;
        }
        this.applyContactDamageToUnit(unit, nearestEnemy, damage);
      }
    } else {
      // Not released: Stay near base
      const targetY = baseLine - POSITIONS.CHARGER_BASE_DISTANCE;
      if (unit.y < targetY) {
        unit.y += moveDistance; // Move back toward base
      }
    }
    
    unit.graphics.x = unit.x;
    unit.graphics.y = unit.y;
  }

  private moveDefault(unit: Unit, nearestEnemy: Enemy): void {
    // Default movement for other unit types
    const moveDistance = unit.getStats().speed * MOVEMENT.FRAME_RATE;
    const angle = Phaser.Math.Angle.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
    
    unit.x += Math.cos(angle) * moveDistance;
    unit.y += Math.sin(angle) * moveDistance;
    
    unit.graphics.x = unit.x;
    unit.graphics.y = unit.y;
  }

  private updateChargerMovementState(): void {
    this.playerUnits.forEach(unit => {
      if (unit.getUnitType() === 'charger') {
        const isReleased = this.isChargerReleased(unit);
        const isMoving = isReleased && this.enemies.length > 0;
        unit.setIsMoving(isMoving);
      }
    });
  }

  private handleTankContactDamage(): void {
    // Player tanks vs enemy tanks contact damage
    this.playerUnits.forEach(unit => {
      if (unit.getUnitType() === 'tank') {
        this.enemies.forEach(enemy => {
          if (enemy.getEnemyType() === 'melee' || enemy.getEnemyType() === undefined) {
            const distance = Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y);
            if (distance < COMBAT_MECHANICS.TANK_CONTACT_RANGE) {
              if (unit.canAttack(COMBAT_MECHANICS.TANK_ATTACK_COOLDOWN) && 
                  enemy.canAttack(COMBAT_MECHANICS.TANK_ATTACK_COOLDOWN)) {
                // Both apply damage
                this.applyContactDamageToUnit(unit, enemy, unit.getStats().damage);
                this.applyContactDamageToEnemy(enemy, unit, enemy.getStats().damage);
                unit.setLastAttackTime(Date.now());
                enemy.setLastAttackTime(Date.now());
              }
            }
          }
        });
      }
    });
  }

  private handleRangedCombat(): void {
    // Player ranged units attack
    this.playerUnits.forEach(unit => {
      if (unit.getUnitType() === 'ranged') {
        const nearestEnemy = this.findNearestEnemy(unit);
        if (nearestEnemy && this.checkInRange(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y, unit.getStats().range)) {
          if (unit.canAttack(COMBAT_MECHANICS.RANGED_ATTACK_COOLDOWN)) {
            this.performRangedAttack(unit, nearestEnemy);
            unit.setLastAttackTime(Date.now());
          }
        }
      }
    });

    // Enemy ranged units attack
    this.enemies.forEach(enemy => {
      if (enemy.getEnemyType() === 'ranged') {
        const nearestUnit = this.findNearestUnit(enemy);
        if (nearestUnit && this.checkInRange(enemy.x, enemy.y, nearestUnit.x, nearestUnit.y, enemy.getStats().range)) {
          if (enemy.canAttack(COMBAT_MECHANICS.RANGED_ATTACK_COOLDOWN)) {
            this.performRangedAttack(enemy, nearestUnit);
            enemy.setLastAttackTime(Date.now());
          }
        }
      }
    });
  }

  private handleChargerCombat(): void {
    this.playerUnits.forEach(unit => {
      if (unit.getUnitType() === 'charger') {
        const nearestEnemy = this.findNearestEnemy(unit);
        if (nearestEnemy) {
          const distance = Phaser.Math.Distance.Between(unit.x, unit.y, nearestEnemy.x, nearestEnemy.y);
          if (distance < MOVEMENT.CHARGER_COLLISION_DISTANCE) {
            // Chargers do double damage when moving
            let damage = unit.getStats().damage;
            if (unit.getIsMoving()) {
              damage *= COMBAT_MECHANICS.CHARGER_MOVING_DAMAGE_MULTIPLIER;
            }
            this.applyContactDamageToUnit(unit, nearestEnemy, damage);
          }
        }
      }
    });
  }

  private applyContactDamageToUnit(attacker: any, target: Enemy, damage: number): void {
    if (target.takeDamage) {
      target.takeDamage(damage);
      if (target.getStats && target.getStats().health <= 0) {
        if (attacker.addKill) {
          attacker.addKill();
        }
        this.scene.events.emit('enemy-destroyed', target);
      }
    }
  }

  private applyContactDamageToEnemy(_attacker: Enemy, target: Unit, damage: number): void {
    if (target.takeDamage) {
      let finalDamage = damage;
      
      // Check if target is a charger and apply damage reduction if moving
      if (target.getUnitType() === 'charger' && target.getIsMoving()) {
        finalDamage = damage * COMBAT_MECHANICS.CHARGER_MOVING_DAMAGE_REDUCTION;
      }
      
      target.takeDamage(finalDamage);
      if (target.getStats && target.getStats().health <= 0) {
        // Unit died, handle unit removal
        this.removeUnit(target);
      }
    }
  }

  private performRangedAttack(attacker: any, target: any): void {
    // For now, apply instant damage (we can add projectiles later)
    const damage = attacker.getStats().damage;
    if (target instanceof Enemy) {
      this.applyContactDamageToUnit(attacker, target, damage);
    } else {
      this.applyContactDamageToEnemy(attacker, target, damage);
    }
  }



  private isChargerReleased(unit: Unit): boolean {
    return this.releasedChargers.has(unit);
  }

  public releaseChargers(): void {
    // Release all charger units
    const chargers = this.playerUnits.filter(unit => unit.getUnitType() === 'charger');
    chargers.forEach(charger => {
      this.releasedChargers.add(charger);
    });
  }

  private checkInRange(x1: number, y1: number, x2: number, y2: number, range: number): boolean {
    const distance = Phaser.Math.Distance.Between(x1, y1, x2, y2);
    return distance <= range;
  }

  private findTankFrontLineForRanged(): number {
    const tanks = this.playerUnits.filter(unit => unit.getUnitType() === 'tank');
    if (tanks.length === 0) {
      return this.scene.cameras.main.height - POSITIONS.UNIT_SPAWN_Y_OFFSET;
    }
    return Math.min(...tanks.map(tank => tank.y));
  }
}
